import aliasFile from '../data/indicator-aliases.json'
import { contentHash } from './hash'
import { MAX_CHECKS, MAX_DEPTH, deeperThan } from './limits'
import { parseTimestamp } from './timestamp'
import { resolveVerdict } from './verdict'
import type {
  AgentRef,
  Check,
  Report,
  ReportNote,
  ReportSource,
  SoftwareRef,
  ToolRef,
} from './types'

/**
 * Turning an assessment document into the normalised shape.
 *
 * Real documents diverge from the published schema in small, consistent ways:
 * the creator may be written as an author, the licence may be a bare string
 * rather than a reference, a tool version may be under either of two keys, and
 * the title may be missing altogether. None of that is worth rejecting a
 * document over, so each divergence is absorbed here and, where it matters,
 * recorded as a note for the data quality view.
 */

export class NotAnAssessmentError extends Error {}

type Json = Record<string, unknown>

export function looksLikeAssessment(value: unknown): value is Json {
  if (!isObject(value)) return false
  const type = stringOf(value['@type'])
  if (type && type.toLowerCase().includes('softwarequalityassessment')) return true
  // Fall back on shape, since @type is occasionally absent or namespaced oddly.
  return Array.isArray(value.checks) && isObject(value.assessedSoftware)
}

export function normaliseReport(input: unknown, source: ReportSource): Report {
  if (!looksLikeAssessment(input)) {
    throw new NotAnAssessmentError('Not a software quality assessment document')
  }
  if (deeperThan(input, MAX_DEPTH)) {
    throw new NotAnAssessmentError(`This document nests deeper than ${MAX_DEPTH} levels.`)
  }

  const notes: ReportNote[] = []
  const software = readSoftware(input.assessedSoftware, notes)
  const createdAt = readTimestamp(input.dateCreated, notes)
  const checks = readChecks(input.checks, notes)
  const contextVersion = readContextVersion(input['@context'])

  if (contextVersion && contextVersion !== CURRENT_CONTEXT_VERSION) {
    notes.push({
      kind: 'legacyContext',
      detail: `Document declares vocabulary ${contextVersion}; current is ${CURRENT_CONTEXT_VERSION}.`,
    })
  }

  return {
    id: contentHash(input),
    source,
    title: buildTitle(input, software),
    software,
    creator: readCreator(input),
    license: readLicense(input.license),
    contextVersion,
    createdAt,
    checks,
    raw: input,
    notes,
  }
}

const CURRENT_CONTEXT_VERSION = '0.0.3'

/**
 * Documents carry no title, so one is synthesised. An explicit name is used
 * when present, since a future generator may start providing one.
 */
function buildTitle(input: Json, software: SoftwareRef): string {
  const declared = stringOf(input.name)
  if (declared) return declared
  return software.version ? `${software.name} ${software.version}` : software.name
}

function readSoftware(value: unknown, notes: ReportNote[]): SoftwareRef {
  if (!isObject(value)) {
    notes.push({ kind: 'unexpectedShape', detail: 'Assessed software is missing.' })
    return { name: 'Unnamed software', key: softwareKey(undefined, undefined) }
  }
  const name = stringOf(value.name)
  if (!name) {
    notes.push({ kind: 'missingSoftwareName', detail: 'Assessed software has no name.' })
  }
  const url = stringOf(value.url)
  return {
    name: name ?? 'Unnamed software',
    version: stringOf(value.softwareVersion) ?? stringOf(value.version),
    // Rendered as a link, so only a web address is kept. Identity still uses
    // what was written, so the key does not change with this rule.
    url: webAddress(url),
    identifier: readReference(value['schema:identifier'] ?? value.identifier),
    key: softwareKey(name, url),
  }
}

/** The value if it is an http(s) address, so it can safely become a link. */
export function webAddress(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  try {
    const { protocol } = new URL(value)
    return protocol === 'https:' || protocol === 'http:' ? value : undefined
  } catch {
    return undefined
  }
}

/**
 * Identity for a piece of software across assessments.
 *
 * Prefers the repository address, which is stable and unique; falls back to a
 * normalised name when there is none. Trailing slashes, a .git suffix and case
 * differences all describe the same repository and must not split it in two.
 */
export function softwareKey(name: string | undefined, url: string | undefined): string {
  if (url) {
    try {
      const parsed = new URL(url)
      const path = parsed.pathname
        .replace(/\.git$/, '')
        .replace(/\/+$/, '')
        .toLowerCase()
      return `${parsed.hostname.toLowerCase()}${path}`
    } catch {
      // Not an address; fall through to the name.
    }
  }
  return `name:${(name ?? 'unnamed software').trim().toLowerCase()}`
}

/** The creator is written as `creator` in the schema but as `author` by some generators. */
function readCreator(input: Json): AgentRef | undefined {
  const value = input.creator ?? input.author
  if (!isObject(value)) {
    const plain = stringOf(value)
    return plain ? { name: plain } : undefined
  }
  return {
    name: stringOf(value.name),
    email: stringOf(value.email) ?? stringOf(value['schema:email']),
  }
}

/** The licence may be a reference object or a bare identifier string. */
function readLicense(value: unknown): string | undefined {
  return readReference(value) ?? stringOf(value)
}

function readTimestamp(value: unknown, notes: ReportNote[]) {
  if (value === undefined || value === null) {
    notes.push({ kind: 'missingTimestamp', detail: 'Document has no creation date.' })
    return undefined
  }
  const parsed = parseTimestamp(value)
  if (!parsed) {
    notes.push({
      kind: 'unparsableTimestamp',
      detail: `Creation date could not be read: ${JSON.stringify(value)}`,
    })
  }
  return parsed
}

function readChecks(value: unknown, notes: ReportNote[]): Check[] {
  if (!Array.isArray(value)) {
    notes.push({ kind: 'unexpectedShape', detail: 'Document has no list of checks.' })
    return []
  }
  if (value.length > MAX_CHECKS) {
    throw new NotAnAssessmentError(
      `This document lists ${value.length} checks; real assessments carry tens, and this reads at most ${MAX_CHECKS}.`,
    )
  }
  return value.map((entry, index) => readCheck(entry, index, notes)).filter(isPresent)
}

function readCheck(entry: unknown, index: number, notes: ReportNote[]): Check | undefined {
  if (!isObject(entry)) {
    notes.push({ kind: 'unexpectedShape', detail: `Check ${index} is not an object.` })
    return undefined
  }

  const id = `c${index}`
  const indicatorUri = canonicalIndicator(readReference(entry.assessesIndicator) ?? '')
  if (!indicatorUri) {
    notes.push({
      kind: 'missingIndicator',
      detail: `Check ${index} does not name an indicator.`,
      checkId: id,
    })
  }

  const output = stringOf(entry.output)
  const statusUri = readReference(entry.status) ?? stringOf(entry.status)
  const { verdict, source } = resolveVerdict({
    output,
    declared: entry.success,
    status: statusUri,
  })
  const rawValue = stringOf(entry.raw_value) ?? stringOf(entry.rawValue)
  const threshold = stringOf(entry.threshold)

  if (source === 'toolFailed') {
    notes.push({
      kind: 'toolFailed',
      detail: `${readTool(entry.checkingSoftware).name} did not complete this check${output ? ` (output "${output}")` : ''}.`,
      checkId: id,
    })
  } else if (verdict === 'unknown' && output !== undefined) {
    notes.push({
      kind: 'unknownOutput',
      detail: `Output "${output}" is not in the verdict vocabulary.`,
      checkId: id,
    })
  }

  return {
    id,
    indicatorUri,
    indicatorSlug: lastSegment(indicatorUri),
    tool: readTool(entry.checkingSoftware),
    process: stringOf(entry.process),
    output,
    evidence: stringOf(entry.evidence),
    rawValue,
    threshold,
    measured: readMeasurement(rawValue),
    statusUri,
    verdict,
    verdictSource: source,
  }
}

const INDICATOR_ALIASES: Record<string, string> = (aliasFile as { aliases: Record<string, string> })
  .aliases

/** Rewrites a misspelt indicator to the one the registry publishes, keeping the rest of the URI. */
function canonicalIndicator(uri: string): string {
  const slug = lastSegment(uri)
  const corrected = INDICATOR_ALIASES[slug]
  if (!corrected) return uri
  const at = uri.lastIndexOf(slug)
  return uri.slice(0, at) + corrected + uri.slice(at + slug.length)
}

/** Tool version appears under either key depending on the generator. */
function readTool(value: unknown): ToolRef {
  if (!isObject(value)) {
    const plain = stringOf(value)
    return { name: plain ?? 'Unknown tool' }
  }
  return {
    name: stringOf(value.name) ?? 'Unknown tool',
    version: stringOf(value.version) ?? stringOf(value.softwareVersion),
    id: stringOf(value['@id']),
  }
}

function readContextVersion(value: unknown): string | undefined {
  const text = readReference(value) ?? stringOf(value)
  if (!text) return undefined
  const match = /(\d+\.\d+\.\d+)/.exec(text)
  return match ? match[1] : undefined
}

/** Reads a JSON-LD reference, which may be a bare string, an object, or a list. */
export function readReference(value: unknown): string | undefined {
  if (typeof value === 'string') return value.trim() || undefined
  if (Array.isArray(value)) return value.length ? readReference(value[0]) : undefined
  if (isObject(value)) {
    const id = value['@id'] ?? value.id
    return typeof id === 'string' ? id.trim() || undefined : undefined
  }
  return undefined
}

/**
 * Pulls a figure out of a raw value.
 *
 * The field is prose with numbers in it, for example
 * "Percentage of duplication: 25.44%; 57 duplicated fragments." Taking the
 * first number is a guess, and a wrong one whenever the sentence leads with
 * something else, so anything derived from this is labelled as interpreted and
 * the original text is always shown beside it.
 */
function readMeasurement(text: string | undefined): number | undefined {
  if (!text) return undefined
  const match = /-?\d+(?:[.,]\d+)?/.exec(text)
  if (!match) return undefined
  const value = Number.parseFloat(match[0].replace(',', '.'))
  return Number.isFinite(value) ? value : undefined
}

export function lastSegment(uri: string): string {
  if (!uri) return ''
  return uri.split('/').filter(Boolean).pop()?.trim() ?? ''
}

function isObject(value: unknown): value is Json {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function stringOf(value: unknown): string | undefined {
  if (typeof value === 'string') return value.trim() || undefined
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return undefined
}

function isPresent<T>(value: T | undefined): value is T {
  return value !== undefined
}
